import ReactECharts from 'echarts-for-react';
import type { StyleConfig, DateGranularity } from '../../../../types/bi';
import { useEChartsTheme } from '../../hooks/useEChartsTheme';
import { formatCategory } from '../../utils/dateFormat';
import { buildChartClickHandler } from '../../utils/drilldown';
import type { DrilldownEvent } from '../../utils/drilldown';

function pieLabelFormatter(content?: StyleConfig['label_content']): string {
  switch (content) {
    case 'name':
      return '{b}';
    case 'value':
      return '{c}';
    case 'percentage':
      return '{d}%';
    case 'name_value':
      return '{b}: {c}';
    case 'value_pct':
      return '{c} ({d}%)';
    case 'all':
      return '{b}\n{c} ({d}%)';
    case 'name_pct':
    default:
      return '{b}\n{d}%';
  }
}

function legendPlacement(pos?: StyleConfig['legend_position']) {
  if (pos === 'none') return { show: false };
  const orient = pos === 'left' || pos === 'right' ? 'vertical' : 'horizontal';
  const placement: Record<string, unknown> = { show: true, orient };
  if (pos === 'top') placement.top = 0;
  else if (pos === 'left') {
    placement.left = 0;
    placement.top = 'center';
  } else if (pos === 'right') {
    placement.right = 0;
    placement.top = 'center';
  } else placement.bottom = 0;
  return placement;
}

interface PieChartProps {
  data: { columns: string[]; rows: Record<string, unknown>[] };
  dimensions: string[];
  measures: { alias: string }[];
  style?: StyleConfig;
  donut?: boolean;
  dateGranularity?: DateGranularity;
  onDrillDown?: (event: DrilldownEvent) => void;
}

export default function PieChart({
  data,
  dimensions,
  measures,
  style,
  donut = false,
  dateGranularity,
  onDrillDown,
}: PieChartProps) {
  const theme = useEChartsTheme();

  const dimKey = dimensions[0] || data.columns[0];
  const measureKey = measures[0]?.alias || data.columns[1];

  const showLabels = style?.show_data_labels ?? true;
  const labelFmt = pieLabelFormatter(style?.label_content);
  const labelPos = style?.label_position || 'outside';

  // Use theme-aware text color so labels are readable in dark mode
  const labelColor =
    getComputedStyle(document.documentElement).getPropertyValue('--text-primary').trim() ||
    undefined;

  const pieData = data.rows.map((r, i) => ({
    name: formatCategory(String(r[dimKey] ?? ''), dateGranularity),
    value: Number(r[measureKey]) || 0,
    ...(style?.colours?.[i] ? { itemStyle: { color: style.colours[i] } } : {}),
  }));

  const option = {
    tooltip:
      style?.tooltip_trigger === 'none'
        ? { show: false }
        : { trigger: 'item' as const, formatter: `{b}: {c} ({d}%)` },
    legend: legendPlacement(style?.legend_position),
    series: [
      {
        type: 'pie' as const,
        radius: donut ? ['45%', '70%'] : '70%',
        center: ['50%', '45%'],
        data: pieData,
        label: {
          show: showLabels,
          formatter: labelFmt,
          position: labelPos,
          color: labelColor,
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.2)',
          },
        },
      },
    ],
  };

  const events = buildChartClickHandler(dimensions, onDrillDown);

  return (
    <ReactECharts
      option={option}
      theme={theme}
      style={{ height: '100%', width: '100%' }}
      onEvents={events}
    />
  );
}
